import React, { useEffect, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { Plus, Trash2, Download } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, AreaChart, Area } from "recharts";

const CATS_IN = ["Hizmet Bedeli", "Reklam Komisyonu", "Eğitim Ücreti", "Danışmanlık", "Diğer Gelir"];
const CATS_OUT = ["Kira", "Maaş", "Yazılım Aboneliği", "Reklam Bütçesi", "Vergi", "Diğer Gider"];

const fmt = (n) => new Intl.NumberFormat("tr-TR").format(Math.round(n ?? 0));
const tl = (n) => `${fmt(n)} ₺`;

export default function FinancePage() {
  const [items, setItems] = useState([]); const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ type: "gider", category: CATS_OUT[0], amount: 0, description: "", date: new Date().toISOString().slice(0,10) });
  const [err, setErr] = useState("");

  const load = () => Promise.all([
    apiClient.get(`/finance${filter !== "all" ? `?type=${filter}` : ""}`).then(r=>r.data),
    apiClient.get("/finance/summary").then(r=>r.data),
  ]).then(([i,s]) => { setItems(i); setSummary(s); }).catch(e => setErr(formatApiError(e)));

  useEffect(() => { load(); }, [filter]);

  const add = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await apiClient.post("/finance", { ...form, amount: Number(form.amount) || 0 });
      setForm({ ...form, amount: 0, description: "" }); load();
    } catch (er) { setErr(formatApiError(er)); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await apiClient.delete(`/finance/${id}`); load(); };

  return (
    <div className="space-y-5" data-testid="finance-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h1 className="text-2xl font-bold">Gelir & Gider</h1><p className="text-sm text-[#64748B] mt-1">Finansal hareket kaydı ve analiz</p></div>
        <button onClick={async ()=>{ const r = await apiClient.get("/export/finance.csv", { responseType: "blob" }); const blob = new Blob([r.data], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "finans.csv"; a.click(); URL.revokeObjectURL(url); }} className="ky-btn ky-btn-outline" data-testid="finance-export-csv"><Download size={13}/>CSV İndir</button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="Toplam Gelir" value={tl(summary.total_income)} tone="success"/>
          <KPI label="Toplam Gider" value={tl(summary.total_expense)} tone="danger"/>
          <KPI label="Net" value={tl(summary.net)} tone={summary.net >= 0 ? "indigo" : "danger"}/>
          <KPI label="Kayıt Sayısı" value={items.length}/>
        </div>
      )}

      {summary && summary.monthly.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 ky-card p-5">
            <h3 className="font-semibold text-[15px] mb-4">Aylık Hareket</h3>
            <div className="h-72"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={summary.monthly}>
                <CartesianGrid stroke="#ECECEF" strokeDasharray="3 3"/>
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={11}/>
                <YAxis stroke="#94A3B8" fontSize={11}/>
                <Tooltip contentStyle={{ background: "white", border: "1px solid #ECECEF", borderRadius: 8 }} formatter={(v)=>tl(v)}/>
                <Bar dataKey="gelir" fill="#10B981" radius={[4,4,0,0]}/>
                <Bar dataKey="gider" fill="#EF4444" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer></div>
          </div>
          <div className="ky-card p-5">
            <h3 className="font-semibold text-[15px] mb-4">Kategori Dağılımı</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(summary.by_category||[]).sort((a,b)=>b.amount-a.amount).map((c,i) => (
                <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b border-[#ECECEF] last:border-0">
                  <div><span className={`ky-badge ${c.type==='gelir'?'ky-badge-success':'ky-badge-danger'}`}>{c.type}</span><span className="ml-2">{c.category}</span></div>
                  <span className="font-mono tabular-nums">{tl(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 ky-card overflow-hidden">
          <div className="px-5 py-3 border-b border-[#ECECEF] flex items-center gap-2 bg-[#FAFAFB]">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Hareketler</div>
            <div className="flex items-center gap-1 ml-auto">
              {["all","gelir","gider"].map(t => <button key={t} onClick={()=>setFilter(t)} className={`ky-tab ${filter===t?"ky-tab-active":""}`}>{t==="all"?"Tümü":t==="gelir"?"Gelir":"Gider"}</button>)}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFB] border-b border-[#ECECEF]">
              <tr><th className="text-left text-[11px] uppercase font-semibold text-[#64748B] px-5 py-2">Tarih</th><th className="text-left text-[11px] uppercase font-semibold text-[#64748B] px-5 py-2">Tip</th><th className="text-left text-[11px] uppercase font-semibold text-[#64748B] px-5 py-2">Kategori</th><th className="text-right text-[11px] uppercase font-semibold text-[#64748B] px-5 py-2">Tutar</th><th className="text-right text-[11px] uppercase font-semibold text-[#64748B] px-5 py-2"></th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-[#94A3B8]">Hareket yok.</td></tr>}
              {items.map(it => (
                <tr key={it.id} className="border-b border-[#ECECEF] ky-row">
                  <td className="px-5 py-2.5 font-mono text-xs">{new Date(it.date).toLocaleDateString("tr-TR")}</td>
                  <td className="px-5 py-2.5"><span className={`ky-badge ${it.type==='gelir'?'ky-badge-success':'ky-badge-danger'}`}>{it.type}</span></td>
                  <td className="px-5 py-2.5">{it.category}<div className="text-[11px] text-[#94A3B8]">{it.description}</div></td>
                  <td className={`px-5 py-2.5 text-right font-mono font-semibold ${it.type==='gelir'?'text-[#10B981]':'text-[#EF4444]'}`}>{it.type==='gelir'?'+':'-'}{tl(it.amount)}</td>
                  <td className="px-5 py-2.5 text-right"><button onClick={()=>del(it.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={12}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={add} className="ky-card p-5 h-fit" data-testid="finance-add-form">
          <h3 className="font-semibold text-[15px] mb-3">Yeni Hareket</h3>
          {err && <div className="text-sm px-3 py-2 mb-3 rounded-md bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">{err}</div>}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={()=>setForm({...form,type:"gelir",category:CATS_IN[0]})} className={`ky-btn ${form.type==='gelir'?'ky-btn-accent':'ky-btn-outline'}`}>Gelir</button>
              <button type="button" onClick={()=>setForm({...form,type:"gider",category:CATS_OUT[0]})} className={`ky-btn ${form.type==='gider'?'ky-btn-accent':'ky-btn-outline'}`}>Gider</button>
            </div>
            <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Kategori</label><select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className="ky-select">{(form.type==='gelir'?CATS_IN:CATS_OUT).map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Tarih</label><input type="date" required value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className="ky-input"/></div>
            <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Tutar (₺) *</label><input type="number" required value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} className="ky-input font-mono" data-testid="finance-amount"/></div>
            <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Açıklama</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="ky-textarea" rows={2}/></div>
            <button type="submit" className="ky-btn ky-btn-accent w-full" data-testid="finance-add"><Plus size={13}/>Ekle</button>
          </div>
        </form>
      </div>
    </div>
  );
}
function KPI({ label, value, tone }) { const cls = tone==="success"?"text-[#10B981]":tone==="danger"?"text-[#EF4444]":tone==="indigo"?"text-[#4F46E5]":"text-[#0F172A]"; return <div className="ky-card p-5"><div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</div><div className={`text-2xl font-semibold tabular-nums mt-2 ${cls}`}>{value}</div></div>; }
